import {
    Step,
    StepButton,
    StepConnector,
    StepLabel,
    Stepper,
    stepConnectorClasses,
    stepLabelClasses,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
    Business,
    CheckCircleOutline,
    ContactPhone,
    MailOutline,
    PeopleAlt,
} from '@mui/icons-material'

const STEP_ICONS = [Business, PeopleAlt, MailOutline, ContactPhone, CheckCircleOutline]

const STEP_TEST_IDS = [
    'send-step-company',
    'send-step-recipients',
    'send-step-sender',
    'send-step-contacts',
    'send-step-confirmation',
]

const ICON_SIZE = 50

function gradientOf(theme, angle) {
    const from = theme.palette.secondary.main
    const to = theme.palette.primary.main

    return `linear-gradient(${angle}deg, ${from}, ${to})`
}

function alongTheLine(theme) {
    return theme.direction === 'rtl' ? 270 : 90
}

function acrossTheIcon(theme) {
    return theme.direction === 'rtl' ? 224 : 136
}

const GradientConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: ICON_SIZE / 2 - 2,
        left: `calc(-50% + ${ICON_SIZE / 2 + 6}px)`,
        right: `calc(50% + ${ICON_SIZE / 2 + 6}px)`,
    },
    [`& .${stepConnectorClasses.line}`]: {
        height: 4,
        borderWidth: 0,
        borderRadius: 2,
        opacity: 1,
        backgroundColor: theme.palette.grey[300],
    },
    [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
        backgroundColor: theme.palette.grey[500],
    },
    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
        backgroundImage: gradientOf(theme, alongTheLine(theme)),
    },
}))

const LABEL_SX = {
    marginTop: 1,
    fontSize: '0.875rem',
    textTransform: 'none',
    fontWeight: 400,
    color: 'text.primary',
    [`&.${stepLabelClasses.completed}`]: {
        fontWeight: 400,
        color: 'text.secondary',
    },
    [`&.${stepLabelClasses.active}`]: {
        fontWeight: 700,
        color: 'text.primary',
    },
}

const NOT_FORWARDED =['active', 'completed']

const StepIconRoot = styled('div', {
    shouldForwardProp: (prop) => !NOT_FORWARDED.includes(prop),
})(({ theme, active, completed }) => {
    const base = {
        zIndex: 1,
        width: ICON_SIZE,
        height: ICON_SIZE,
        boxSizing: 'border-box',
        display: 'flex',
        borderRadius: '50%',
        justifyContent: 'center',
        alignItems: 'center',
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.common.white,
        border: `2px solid ${theme.palette.primary.main}`,
    }

    if (active) {
        return {
            ...base,
            border: 'none',
            color: theme.palette.common.white,
            backgroundImage: gradientOf(theme, acrossTheIcon(theme)),
            boxShadow: '0 4px 10px 0 rgba(0, 0, 0, .25)',
        }
    }

    if (completed) {
        return {
            ...base,
            border: 'none',
            color: theme.palette.grey[700],
            backgroundColor: theme.palette.grey[400],
        }
    }

    return base
})

function makeStepIcon(Glyph) {
    return function StepIcon({ active, completed }) {
        return (
            <StepIconRoot active={active} completed={completed}>
                <Glyph />
            </StepIconRoot>
        )
    }
}

const ICONS = STEP_ICONS.map(makeStepIcon)

export default function SendStepper({
    labels,
    activeStep,
    completed,
    onStep,
}) {
    return (
        <Stepper
            nonLinear
            alternativeLabel
            activeStep={activeStep}
            connector={<GradientConnector />}
            sx={{
                marginBottom: 4,
                padding: 0,
                background: 'none',
                boxShadow: 'none',
                borderRadius: 0,
            }}
        >
            {labels.map((label, index) => (
                <Step key={label} completed={completed[index] === true}>
                    <StepButton
                        data-testid={STEP_TEST_IDS[index]}
                        data-completed={completed[index] === true}
                        onClick={() => onStep(index)}
                    >
                        <StepLabel
                            StepIconComponent={ICONS[index]}
                            slotProps={{ label: { sx: LABEL_SX } }}
                        >
                            {label}
                        </StepLabel>
                    </StepButton>
                </Step>
            ))}
        </Stepper>
    )
}
